import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowRight,
  Calendar,
  PenLine,
  Smartphone,
  ShieldCheck,
  ListChecks,
  SlidersHorizontal,
  Workflow,
  Server,
  BarChart3,
  CheckCircle2,
  Plus,
  X,
} from "lucide-react";
import { KineticLines, Reveal, Eyebrow } from "@/components/site/Reveal";

const HERO_BULLETS = [
  { t: "Guaranteed Inbox Placement", d: "Proactive domain warming, SPF/DKIM/DMARC protocol enforcement, and deep spam-filter defense." },
  { t: "Full-Funnel Lifecycle Workflows", d: "From trigger-based onboarding drips to multi-touch sales cadences that guide subscribers toward conversion." },
  { t: "Enterprise Sending Architecture", d: "Dedicated high-volume SMTP relays, managed IP pools, feedback loops, and automated reputation scoring." },
];

const BADGES = [
  "99.2% INBOX PLACEMENT",
  "GOOGLE & YAHOO 2024–2026 COMPLIANT",
  "SPF · DKIM · DMARC ENFORCED",
  "DEDICATED IP POOLS",
];

const PILLARS = [
  { icon: Calendar, t: "Campaign Strategy, Planning & Execution", d: "Every broadcast aligned to bottom-line milestones — pipeline acceleration, trial signups, product announcements, repeat revenue. We own segmentation, campaign mapping, scheduling, production, and post-send analysis." },
  { icon: PenLine, t: "High-Converting Copywriting & Editorial Craft", d: "Most marketing emails are deleted in under three seconds. Our B2B direct-response copywriters craft subject lines that bypass inbox fatigue, curiosity-driven preheaders, and scannable body copy driving to a singular call to action." },
  { icon: Smartphone, t: "Device-Responsive Design & Interactive Layouts", d: "Over 55% of emails open on mobile. We build lightweight, cross-client HTML templates tested across 60+ desktop, mobile, and webmail clients — including dark mode — for pixel-perfect hierarchy and fast loading." },
  { icon: ShieldCheck, t: "Deliverability & Sender Reputation Management", d: "SPF, DKIM, DMARC, BIMI, and custom return-path records established and maintained. Automated IP warm-up ramps, blacklist monitoring (Spamhaus, Barracuda, Invaluement), 95+ sender scores, and FBL processing." },
  { icon: ListChecks, t: "Database Management, List Growth & Data Hygiene", d: "Multi-layered list verification scrubs hard bounces, disposable domains, and spam traps, with automated re-engagement workflows — plus compliant opt-in capture that grows your subscriber base ethically." },
  { icon: SlidersHorizontal, t: "Smart Segmentation & Dynamic Personalization", d: "Granular micro-segments by job role, geography, purchase behavior, interaction history, and lead score. Dynamic merge tokens and conditional content blocks make every message feel personally crafted." },
  { icon: Workflow, t: "Marketing Automation & Drip Sequences", d: "Autonomous, event-driven workflows operating 24/7: multi-stage welcome journeys, lead qualification drips, cart abandonment, webinar reminders, and win-back series that reactivate dormant subscribers." },
  { icon: Server, t: "Dedicated SMTP Infrastructure & Managed IP Rotation", d: "Custom-tailored SMTP servers with automated IP rotation, dedicated IP pools, custom rDNS, and granular throttle management — absolute control over your deliverability destiny." },
  { icon: BarChart3, t: "Advanced Analytics, Attribution & Optimization", d: "Beyond open rates: CTOR, conversion attribution, unsubscribe sentiment, reply volume, and acquisition costs. Systematic multivariate A/B testing on hooks, send-times, designs, and copy length." },
];

const CAMPAIGNS = [
  { m: "B2B Cold Email Outreach", f: "Hyper-targeted outbound to executive decision-makers featuring relevant value propositions, pain-point disruption, and low-friction meeting asks.", i: "Generates predictable sales conversations, books qualified SDR meetings, and expands enterprise pipeline without paid ad spend." },
  { m: "Automated Drip Sequences", f: "Timed multi-message journeys educating new leads step-by-step through case studies, architectural blueprints, and client outcomes.", i: "Nurtures early-stage inquiries into sales-ready prospects automatically over a 30 to 90-day cycle." },
  { m: "Editorial Newsletters", f: "Regular value-driven dispatches delivering industry analysis, tactical insights, and curated proprietary research.", i: "Builds deep domain authority, maintains consistent top-of-mind brand awareness, and lowers customer churn rates." },
  { m: "High-Impact Promotional Blasts", f: "Urgent, time-sensitive campaigns surrounding product releases, feature launches, end-of-quarter offers, or live conferences.", i: "Drives rapid revenue surges, spikes platform adoption, and accelerates pending pipeline deals." },
  { m: "Customer Retention & Win-Backs", f: "Automated re-engagement sequences triggered when subscriber activity drops below predetermined engagement thresholds.", i: "Recovers lapsed enterprise accounts, lowers subscriber attrition, and identifies at-risk accounts before churn occurs." },
];

const PHASES = [
  { t: "Discovery & Technical Audit", s: "Deep Infrastructure & Historical Performance Audit", d: "We examine your sending domains, DNS authentication records (SPF, DKIM, DMARC), sender reputation scores, list quality, and previous campaign metrics — isolating delivery bottlenecks, toxic segments, and immediate quick wins." },
  { t: "Strategy & Funnel Architecture", s: "Strategy Architecture & Strategic Blueprinting", d: "We formulate your comprehensive sending calendar, establish segment rules, outline automation flowcharts, and draft conversion-focused messaging frameworks aligned with your commercial objectives." },
  { t: "Agile Production & Sending", s: "Production, Testing & Controlled Deployment", d: "Copywriters and HTML engineers produce campaign assets, pre-tested through spam-filter preview engines (Litmus/Email on Acid), validated against suppression files, and executed with optimized send-time algorithms and volume throttling." },
  { t: "Analysis & Multivariate Refinement", s: "Post-Send Telemetry & Iterative Refinement", d: "Within 48 hours of dispatch, we compile detailed telemetry reports — clicks, heatmaps, negative signals — and feed these behavioral insights directly into the next campaign cycle." },
];

const SMTP_ROWS = [
  { dim: "IP Reputation Control", shared: "Shared with thousands of random unknown senders; vulnerable to reputation poisoning.", vy: "100% dedicated IP allocation isolated solely for your organization's sending activity." },
  { dim: "Volume Throttling & Restrictions", shared: "Arbitrary sending caps, sudden account suspensions, and rigid tier pricing limits.", vy: "Uncapped sending throughput customized to your target list volume with automated warm-up scripts." },
  { dim: "Custom Technical Configuration", shared: "Standardized DNS setup with minimal control over low-level mail transfer agent (MTA) rules.", vy: "Granular server configuration: customized rDNS, bounce handling rules, MTA tuning, and automated IP rotation." },
  { dim: "Cost Efficiency at Scale", shared: "Monthly subscription costs multiply exponentially as subscriber databases grow into the hundreds of thousands.", vy: "Fixed infrastructure cost with significantly lower cost per thousand emails sent (CPM)." },
];

const FAQS = [
  { q: "How do you protect our primary corporate domain during outbound and cold email campaigns?", a: "We never send high-volume cold outreach from your primary corporate root domain. Instead, we configure lookalike secondary domains (e.g., yourcompany-tech.com) with complete SPF, DKIM, and DMARC authentication, forward all incoming web traffic to your primary site, and warm each inbox gradually before initiating sending." },
  { q: "What is the typical timeframe required to warm up a dedicated IP address?", a: "A disciplined IP warming ramp requires approximately 14 to 28 days depending on your intended peak sending volume. We begin by routing your highest-engaged subscribers through the new IP, progressively scaling volume every 48 hours to establish a sterling sender history with major ISPs." },
  { q: "How does Vyacomm Media manage compliance across international privacy laws like GDPR and CAN-SPAM?", a: "Every broadcast managed by Vyacomm Media includes verifiable physical corporate address headers, instantaneous single-click unsubscribe links, and automated suppression list updates. For European recipients, campaigns operate under documented legitimate interest or explicit consent frameworks with full audit records." },
  { q: "Can you integrate directly with our existing CRM and marketing automation platforms?", a: "Yes. We integrate seamlessly with HubSpot, Salesforce, Marketo, ActiveCampaign, Klaviyo, and custom PostgreSQL/MySQL databases via REST APIs and real-time webhooks." },
];

const BARS = [34, 48, 40, 58, 52, 66, 60, 74, 68, 82, 76, 88];

const EmailDashboard = () => (
  <motion.div
    initial={{ opacity: 0, y: 32 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay: 0.6, duration: 1, ease: [0.22, 1, 0.36, 1] }}
    className="w-full max-w-[480px] rounded-2xl border border-white/[0.09] bg-[#0e1e33]/70 shadow-[0_10px_28px_-24px_rgba(2,6,17,0.7)] backdrop-blur-sm"
    data-testid="email-dashboard-preview"
  >
    <div className="flex items-center justify-between border-b border-white/[0.07] px-5 py-3.5">
      <span className="font-mono2 text-[9px] tracking-[0.22em] text-slate-400">SENDER TELEMETRY</span>
      <span className="flex items-center gap-1.5 font-mono2 text-[9px] tracking-[0.2em] text-slate-400">
        <span className="live-pulse text-emerald-400">●</span> LIVE
      </span>
    </div>
    <div className="grid grid-cols-2 gap-px bg-white/[0.06]">
      <div className="bg-[#0d1c31] px-5 py-4">
        <p className="font-mono2 text-[8px] tracking-[0.2em] text-slate-500">SENDER REPUTATION</p>
        <p className="mt-1 font-jakarta text-2xl font-extrabold text-white">
          98<span className="text-sm font-semibold text-slate-400">/100</span>
        </p>
      </div>
      <div className="bg-[#0d1c31] px-5 py-4">
        <p className="font-mono2 text-[8px] tracking-[0.2em] text-slate-500">INBOX PLACEMENT</p>
        <p className="mt-1 font-jakarta text-2xl font-extrabold text-white">99.2%</p>
      </div>
    </div>
    <div className="px-5 py-5">
      <p className="font-mono2 text-[8px] tracking-[0.2em] text-slate-500">OPEN-TO-CLICK CONVERSION — LAST 12 SENDS</p>
      <div className="mt-3 flex h-20 items-end gap-1.5">
        {BARS.map((h, i) => (
          <motion.span
            key={i}
            initial={{ scaleY: 0 }}
            animate={{ scaleY: 1 }}
            transition={{ delay: 1 + i * 0.05, duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
            className="flex-1 origin-bottom rounded-sm bg-gradient-to-t from-cobalt/40 to-cobaltlight/80"
            style={{ height: `${h}%` }}
          />
        ))}
      </div>
    </div>
    <div className="border-t border-white/[0.07] px-5 py-4">
      <p className="font-mono2 text-[8px] tracking-[0.2em] text-slate-500">ACTIVE DRIP SEQUENCE</p>
      <div className="mt-3 flex items-center">
        {["Welcome", "Nurture", "Demo", "Win-back"].map((s, i) => (
          <div key={s} className="flex flex-1 items-center last:flex-none">
            <div className="flex flex-col items-center gap-1.5">
              <span className={`h-2 w-2 rounded-full ${i < 3 ? "bg-cobalt" : "bg-flame"}`} />
              <span className="font-mono2 text-[8px] tracking-[0.12em] text-slate-400">{s}</span>
            </div>
            {i < 3 && <div className="mx-2 mb-4 h-px flex-1 bg-white/10" />}
          </div>
        ))}
      </div>
    </div>
  </motion.div>
);

const Faq = ({ q, a, i }) => {
  const [open, setOpen] = useState(i === 0);
  return (
    <div className="rounded-2xl border border-line bg-white" data-testid={`email-faq-${i + 1}`}>
      <button
        data-testid={`email-faq-toggle-${i + 1}`}
        onClick={() => setOpen(!open)}
        className="flex w-full items-center justify-between gap-4 px-6 py-5 text-left"
      >
        <span className="font-jakarta text-base font-bold text-navy">{q}</span>
        <motion.span animate={{ rotate: open ? 45 : 0 }} transition={{ duration: 0.3 }} className="shrink-0 text-cobalt">
          <Plus className="h-5 w-5" />
        </motion.span>
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden"
          >
            <p className="px-6 pb-6 text-sm leading-relaxed text-slate-600">{a}</p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default function EmailMarketing() {
  useEffect(() => {
    document.title = "Enterprise Email Marketing & Deliverability Services | Vyacomm Media";
  }, []);

  const scrollToCompare = (e) => {
    e.preventDefault();
    const el = document.getElementById("smtp-comparison");
    if (el && window.__lenis) window.__lenis.scrollTo(el, { offset: -110, duration: 1.2 });
  };

  return (
    <div data-testid="email-marketing-page">
      {/* HERO */}
      <section className="grain hero-grid-lines relative overflow-hidden bg-navy pt-[160px]" data-testid="email-hero">
        <div className="pointer-events-none absolute -right-40 top-20 h-[420px] w-[420px] rounded-full bg-cobalt/15 blur-[140px]" />
        <div className="relative z-10 mx-auto grid max-w-[1808px] items-center gap-14 px-5 pb-20 sm:px-8 lg:grid-cols-2 lg:px-12 2xl:px-14">
          <div>
            <Reveal delay={0.05} y={16}>
              <Eyebrow dark>Full-Stack Email Marketing & Inbox Placement Infrastructure</Eyebrow>
            </Reveal>
            <h1 className="mt-6 font-jakarta text-4xl font-extrabold leading-[1.08] tracking-tight text-white sm:text-5xl lg:text-[56px]">
              <KineticLines lines={["Turn Inboxes into Reliable", "Revenue Engines."]} accentIndex={1} />
            </h1>
            <Reveal delay={0.55}>
              <p className="mt-6 max-w-xl text-base leading-relaxed text-slate-300 sm:text-lg">
                An email that never lands in the primary inbox is an email that never existed. Vyacomm Media delivers
                enterprise-grade email marketing engineered for performance: campaign strategy, compelling copy,
                responsive layouts, lifecycle automation, and dedicated SMTP environments with automated IP rotation.
              </p>
            </Reveal>
            <div className="mt-8 space-y-4">
              {HERO_BULLETS.map((b, i) => (
                <Reveal key={b.t} delay={0.68 + i * 0.1} y={14}>
                  <div className="flex items-start gap-3">
                    <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-flame" />
                    <p className="text-sm leading-relaxed text-slate-300">
                      <span className="font-semibold text-white">{b.t} — </span>
                      {b.d}
                    </p>
                  </div>
                </Reveal>
              ))}
            </div>
            <Reveal delay={1}>
              <div className="mt-9 flex flex-wrap items-center gap-4">
                <Link
                  to="/contact-us"
                  data-testid="email-audit-cta"
                  className="group inline-flex items-center gap-2 rounded-full bg-cobalt px-7 py-4 text-sm font-bold text-white shadow-[0_18px_44px_-12px_rgba(37,99,235,0.8)] transition-all duration-300 hover:scale-[1.03] hover:bg-[#1d4fd7] active:scale-95"
                >
                  Audit My Email Deliverability
                  <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                </Link>
                <a
                  href="#smtp-comparison"
                  onClick={scrollToCompare}
                  data-testid="email-plans-cta"
                  className="inline-flex items-center gap-2 rounded-full border border-white/20 px-7 py-4 text-sm font-semibold text-white transition-all duration-300 hover:border-cobaltlight hover:bg-white/5"
                >
                  Explore SMTP & Campaign Plans
                </a>
              </div>
              <p className="mt-4 font-mono2 text-[10px] tracking-[0.18em] text-slate-400">
                ZERO SENDING DISRUPTIONS · INFRASTRUCTURE AUDIT INCLUDED · GOOGLE & YAHOO SENDER MANDATE COMPLIANT
              </p>
            </Reveal>
          </div>
          <div className="hidden justify-end lg:flex">
            <EmailDashboard />
          </div>
        </div>
        <div className="relative z-10 border-t border-white/10 bg-[#081426]">
          <div className="mx-auto flex max-w-[1808px] flex-wrap items-center justify-center gap-x-10 gap-y-3 px-5 py-5 sm:px-8 lg:px-12 2xl:px-14">
            {BADGES.map((b) => (
              <span key={b} className="flex items-center gap-2 font-mono2 text-[10px] tracking-[0.18em] text-slate-400">
                <ShieldCheck className="h-3.5 w-3.5 text-cobaltlight" />
                {b}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* MODERN EMAIL REALITY */}
      <section className="bg-canvas py-24 sm:py-32" data-testid="email-reality">
        <div className="mx-auto grid max-w-[1808px] gap-12 px-5 sm:px-8 lg:grid-cols-2 lg:px-12 2xl:px-14">
          <Reveal>
            <Eyebrow>The Modern Email Reality</Eyebrow>
            <h2 className="mt-4 font-jakarta text-3xl font-extrabold tracking-tight text-navy sm:text-4xl">
              Why strategic email marketing trumps mass blasting
            </h2>
          </Reveal>
          <Reveal delay={0.12}>
            <div className="space-y-5 text-base leading-relaxed text-slate-600">
              <p>
                In today's inbox ecosystem, blasting generic templates to unverified lists is the fastest path to spam
                traps, domain blacklists, and permanently damaged sender scores. Major mailbox providers — Google
                Workspace, Microsoft 365, Yahoo — enforce rigorous behavioral telemetry, evaluating domain reputation,
                engagement velocity, bounce frequency, and DNS authentication before your message reaches the primary
                inbox.
              </p>
              <p>
                Vyacomm Media approaches email not as an isolated broadcast tool, but as a synchronized, technical
                revenue ecosystem — uniting compelling human storytelling with carrier-grade sending architecture so
                every message reaches its destination, commands attention, and triggers quantifiable commercial action.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* 9 PILLARS */}
      <section className="border-y border-line bg-white py-24 sm:py-32" data-testid="email-pillars">
        <div className="mx-auto max-w-[1808px] px-5 sm:px-8 lg:px-12 2xl:px-14">
          <Reveal>
            <Eyebrow>Core Service Pillars</Eyebrow>
            <h2 className="mt-4 max-w-3xl font-jakarta text-3xl font-extrabold tracking-tight text-navy sm:text-4xl">
              Everything you need under one roof
            </h2>
            <p className="mt-5 max-w-2xl text-base text-slate-600">
              End-to-end capabilities covering every operational, technical, and creative layer required to build and
              sustain high-ROI email programs.
            </p>
          </Reveal>
          <div className="mt-14 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {PILLARS.map((p, i) => (
              <Reveal key={p.t} delay={(i % 3) * 0.07}>
                <div className="h-full rounded-2xl border border-line bg-canvas p-7 transition-all duration-300 hover:-translate-y-1 hover:border-cobalt/50 hover:bg-white">
                  <div className="flex items-center justify-between">
                    <span className="grid h-11 w-11 place-items-center rounded-xl bg-eblue/10 text-cobalt">
                      <p.icon className="h-5 w-5" />
                    </span>
                    <span className="font-mono2 text-xs tracking-[0.2em] text-slate-400">0{i + 1}</span>
                  </div>
                  <h3 className="mt-5 font-jakarta text-base font-bold text-navy">{p.t}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-slate-600">{p.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CAMPAIGN TYPES */}
      <section className="bg-canvas py-24 sm:py-32" data-testid="email-campaigns">
        <div className="mx-auto max-w-[1808px] px-5 sm:px-8 lg:px-12 2xl:px-14">
          <Reveal>
            <Eyebrow>Campaign Types & Operational Playbooks</Eyebrow>
            <h2 className="mt-4 max-w-3xl font-jakarta text-3xl font-extrabold tracking-tight text-navy sm:text-4xl">
              Methodologies matched to your deal complexity and sales cycle
            </h2>
          </Reveal>
          <Reveal delay={0.12} className="mt-12">
            <div className="overflow-hidden rounded-2xl border border-line bg-white">
              <div className="hidden grid-cols-12 gap-6 bg-navy px-6 py-4 sm:grid">
                <span className="col-span-3 font-mono2 text-[10px] tracking-[0.22em] text-slate-400">CAMPAIGN MODEL</span>
                <span className="col-span-5 font-mono2 text-[10px] tracking-[0.22em] text-slate-400">STRATEGIC FOCUS & AUDIENCE</span>
                <span className="col-span-4 font-mono2 text-[10px] tracking-[0.22em] text-flame">TYPICAL BUSINESS IMPACT</span>
              </div>
              {CAMPAIGNS.map((c, i) => (
                <div key={c.m} className={`grid gap-3 border-b border-line px-6 py-6 last:border-b-0 sm:grid-cols-12 sm:gap-6 ${i % 2 === 0 ? "bg-white" : "bg-canvas"}`}>
                  <h3 className="col-span-3 font-jakarta text-sm font-bold text-navy">{c.m}</h3>
                  <p className="col-span-5 text-sm leading-relaxed text-slate-600">{c.f}</p>
                  <p className="col-span-4 text-sm leading-relaxed text-charcoal">{c.i}</p>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* 4-STAGE METHODOLOGY */}
      <section className="grain relative overflow-hidden bg-navy py-24 sm:py-32" data-testid="email-methodology">
        <div className="relative z-10 mx-auto max-w-[1808px] px-5 sm:px-8 lg:px-12 2xl:px-14">
          <Reveal>
            <Eyebrow dark>Our 4-Stage Execution & Optimization Methodology</Eyebrow>
            <h2 className="mt-4 max-w-3xl font-jakarta text-3xl font-extrabold tracking-tight text-white sm:text-4xl">
              Guesswork eliminated through an agile, data-grounded framework
            </h2>
          </Reveal>
          <div className="mt-14 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {PHASES.map((p, i) => (
              <Reveal key={p.t} delay={i * 0.09}>
                <div className="h-full rounded-2xl border border-white/10 bg-white/[0.04] p-7">
                  <span className="font-jakarta text-4xl font-extrabold text-white/15">0{i + 1}</span>
                  <h3 className="mt-4 font-jakarta text-lg font-bold text-white">{p.t}</h3>
                  <p className="mt-1 font-mono2 text-[9px] tracking-[0.18em] text-cobaltlight">{p.s.toUpperCase()}</p>
                  <p className="mt-3 text-sm leading-relaxed text-slate-400">{p.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* SMTP COMPARISON */}
      <section id="smtp-comparison" className="bg-canvas py-24 sm:py-32" data-testid="smtp-comparison-section">
        <div className="mx-auto max-w-[1808px] px-5 sm:px-8 lg:px-12 2xl:px-14">
          <Reveal>
            <Eyebrow>Infrastructure Advantage</Eyebrow>
            <h2 className="mt-4 max-w-3xl font-jakarta text-3xl font-extrabold tracking-tight text-navy sm:text-4xl">
              Dedicated SMTP servers vs. shared email platforms
            </h2>
          </Reveal>
          <Reveal delay={0.12} className="mt-12">
            <div className="overflow-hidden rounded-2xl border border-line bg-white">
              <div className="hidden grid-cols-12 gap-6 bg-navy px-6 py-4 sm:grid">
                <span className="col-span-3 font-mono2 text-[10px] tracking-[0.22em] text-slate-400">OPERATIONAL DIMENSION</span>
                <span className="col-span-4 font-mono2 text-[10px] tracking-[0.22em] text-slate-400">TYPICAL SHARED PLATFORMS</span>
                <span className="col-span-5 font-mono2 text-[10px] tracking-[0.22em] text-flame">VYACOMM DEDICATED SMTP & IP ROTATION</span>
              </div>
              {SMTP_ROWS.map((r) => (
                <div key={r.dim} className="grid gap-4 border-b border-line px-6 py-6 last:border-b-0 sm:grid-cols-12 sm:gap-6">
                  <h3 className="col-span-3 font-jakarta text-sm font-bold text-navy">{r.dim}</h3>
                  <p className="col-span-4 flex items-start gap-2 text-sm leading-relaxed text-slate-500">
                    <X className="mt-0.5 h-4 w-4 shrink-0 text-red-400" />
                    {r.shared}
                  </p>
                  <p className="col-span-5 flex items-start gap-2 text-sm leading-relaxed text-charcoal">
                    <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-500" />
                    {r.vy}
                  </p>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* FAQ */}
      <section className="border-t border-line bg-white py-24 sm:py-32" data-testid="email-faq-section">
        <div className="mx-auto max-w-4xl px-5 sm:px-8">
          <Reveal>
            <Eyebrow>Email Marketing & Infrastructure FAQ</Eyebrow>
            <h2 className="mt-4 font-jakarta text-3xl font-extrabold tracking-tight text-navy sm:text-4xl">
              Technical answers, straight
            </h2>
          </Reveal>
          <div className="mt-12 space-y-4">
            {FAQS.map((f, i) => (
              <Reveal key={f.q} delay={i * 0.06}>
                <Faq q={f.q} a={f.a} i={i} />
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="grain relative overflow-hidden bg-navy py-20" data-testid="email-cta">
        <div className="relative z-10 mx-auto flex max-w-[1808px] flex-col items-start justify-between gap-6 px-5 sm:flex-row sm:items-center sm:px-8 lg:px-12 2xl:px-14">
          <div>
            <h2 className="max-w-xl font-jakarta text-2xl font-extrabold text-white sm:text-3xl">
              Get a free deliverability audit of your current sending infrastructure.
            </h2>
            <p className="mt-2 font-mono2 text-[10px] tracking-[0.2em] text-slate-400">
              DNS AUTHENTICATION REVIEW · REPUTATION SCORING · QUICK-WIN ROADMAP
            </p>
          </div>
          <Link
            to="/contact-us"
            data-testid="email-bottom-cta"
            className="group inline-flex shrink-0 items-center gap-2 rounded-full bg-flame px-7 py-4 text-sm font-bold text-navy transition-all duration-300 hover:scale-[1.03] hover:bg-[#ffb43a]"
          >
            Schedule Strategy Call
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
        </div>
      </section>
    </div>
  );
}
